import { useState, type JSX } from "react";

import { setSoundEnabled, soundEnabled } from "../lib/sound";
import { useVoss } from "../state/VossProvider";
import { Drawer } from "./Drawer";
import { Kbd, Switch } from "./primitives";

const SHORTCUTS: readonly [string, string][] = [
  ["Command palette", "⌘K"],
  ["New chat", "⌘⇧K"],
  ["Toggle theme", "⌘J"],
  ["Toggle debug", "⌘D"],
  ["Settings", "⌘,"],
  ["Focus composer", "/"],
  ["Close", "Esc"],
];

/** Settings drawer: theme, debug, sound, memory transparency, keyboard reference. */
export function Settings({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { theme, toggleTheme, debug, setDebug, memory, fixtureMode } = useVoss();
  const [sound, setSound] = useState(soundEnabled());

  return (
    <Drawer open={open} title="Settings" onClose={onClose}>
      <div className="voss-field">
        <div>
          <div className="voss-field__label">Appearance</div>
          <div className="voss-field__desc">Currently {theme}</div>
        </div>
        <Switch checked={theme === "light"} onChange={toggleTheme} label="Toggle light theme" />
      </div>

      <div className="voss-field">
        <div>
          <div className="voss-field__label">Debug Mode</div>
          <div className="voss-field__desc">Reveal orchestration (capabilities only, never providers)</div>
        </div>
        <Switch checked={debug} onChange={setDebug} label="Toggle debug mode" />
      </div>

      <div className="voss-field">
        <div>
          <div className="voss-field__label">Sound</div>
          <div className="voss-field__desc">Subtle cues on send and reply (off by default)</div>
        </div>
        <Switch
          checked={sound}
          onChange={(next) => {
            setSound(next);
            setSoundEnabled(next);
          }}
          label="Toggle sound"
        />
      </div>

      <div>
        <div className="voss-section-label" style={{ padding: 0 }}>
          Memory {fixtureMode ? "(fixture)" : ""}
        </div>
        <p className="voss-field__desc" style={{ marginBottom: "var(--space-3)" }}>
          User-controlled and workspace-scoped. VOSS only uses what you keep enabled.
        </p>
        {memory.length === 0 ? (
          <p className="voss-empty">No memory in this workspace</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {memory.map((m) => (
              <div key={m.id} className="voss-mem-item" data-disabled={!m.enabled}>
                <div className="voss-mem-item__text">
                  {m.text}
                  <div className="voss-mem-item__tags">
                    {m.scope} · {m.tags.join(", ") || "no tags"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="voss-section-label" style={{ padding: 0 }}>
          Keyboard
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
          {SHORTCUTS.map(([label, key]) => (
            <div key={label} className="voss-field">
              <span className="voss-field__desc">{label}</span>
              <Kbd>{key}</Kbd>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}
